import type { ReactNode } from "react";

import { Inbox } from "lucide-react";

import { cn } from "@/shared/lib/utils";

interface EmptyStateProps {
  /** Icono opcional — default `Inbox`. */
  icon?: ReactNode;
  /** Título — display Fraunces. */
  title: string;
  /** Mensaje bajo el título. */
  description?: ReactNode;
  /** CTA(s): típicamente un botón "Crear …" primary. */
  actions?: ReactNode;
  /** Variantes de intención — ajusta tono visual. */
  variant?: "empty" | "no-results";
  className?: string;
}

/**
 * Empty state unificado. Dos intenciones:
 *   - `empty`: la colección está vacía (ej. "Aún no hay consejos").
 *   - `no-results`: hay datos pero el filtro no matchea ("Sin resultados").
 *
 * Pensado para vivir dentro de la Card del listado, no como página
 * completa. Para páginas completas, envuélvelo en un contenedor.
 */
export function EmptyState({
  icon,
  title,
  description,
  actions,
  variant = "empty",
  className,
}: EmptyStateProps) {
  const Icon = icon ?? <Inbox className="h-6 w-6" />;
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 px-6 py-14 text-center",
        className,
      )}
    >
      <div
        className={cn(
          "flex h-14 w-14 items-center justify-center rounded-2xl",
          variant === "empty"
            ? "bg-primary/10 text-primary ring-1 ring-primary/15"
            : "bg-muted text-muted-foreground",
        )}
      >
        {Icon}
      </div>
      <div className="max-w-sm space-y-1.5">
        <h3 className="font-display text-[1.1rem] font-medium tracking-display-tight text-foreground">
          {title}
        </h3>
        {description ? (
          <p className="text-[13.5px] leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center justify-center gap-2 pt-2">{actions}</div>
      ) : null}
    </div>
  );
}
